import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Lock, CheckCircle, AlertCircle } from 'lucide-react'
import api from '@/lib/api'

export default function SetupAccount() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center space-y-4">
            <AlertCircle size={48} className="text-red-500 mx-auto" />
            <h2 className="text-lg font-semibold text-slate-900">Invalid Setup Link</h2>
            <p className="text-sm text-slate-500">This account setup link is invalid or has expired. Please contact your administrator.</p>
            <Link to="/login" className="inline-block text-sm text-red-800 hover:text-red-900 font-medium">
              Go to Sign In
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }
    setLoading(true)
    try {
      await api.post('/auth/reset-password', { token, new_password: password })
      setSuccess(true)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to set up your password. The link may have expired.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Logo" className="h-16 w-16 mx-auto mb-4 rounded-xl" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
          <h1 className="text-2xl font-bold text-white">Project One</h1>
          <p className="text-slate-400 text-sm mt-1">Set up your account</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {success ? (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <CheckCircle size={48} className="text-green-500" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Account Ready!</h2>
              <p className="text-sm text-slate-500">Your password has been set successfully. You can now sign in with your username and password.</p>
              <Link to="/login"
                className="inline-block w-full py-2.5 bg-red-800 text-white rounded-lg font-medium text-sm hover:bg-red-900 transition-colors text-center">
                Sign In
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="bg-blue-50 text-blue-700 text-sm px-4 py-3 rounded-lg border border-blue-200">
                Create a password to complete your account setup.
              </div>
              {error && (
                <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-lg border border-red-200">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent"
                    placeholder="Create a password" autoFocus required minLength={6} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent"
                    placeholder="Confirm your password" required minLength={6} />
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-2.5 bg-red-800 text-white rounded-lg font-medium text-sm hover:bg-red-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Setting up...' : 'Set Password & Activate Account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
